"use client";

import { useState, useEffect, useRef } from "react";
import { Sparkles, X, AlertCircle, Info, AlertTriangle } from "lucide-react";

type Alert = {
  tipo: string;
  modulo: string;
  severidad: "info" | "warning" | "critical";
  titulo: string;
  mensaje: string;
  entidadTipo?: string;
  entidadId?: string;
};

const POLL_INTERVAL_MS = 5 * 60_000; // 5 min

export default function CaonaboAlertBell() {
  const [open, setOpen] = useState(false);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Click fuera cierra dropdown
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // Polling de alertas
  useEffect(() => {
    let cancel = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    async function load() {
      try {
        setLoading(true);
        const r = await fetch("/api/caonabo/alerts", { cache: "no-store" });
        if (!r.ok) return;
        const data = await r.json();
        if (cancel) return;
        setEnabled(Boolean(data?.enabled));
        if (data?.alerts) setAlerts(data.alerts);
      } catch {
        if (!cancel) setEnabled(false);
      } finally {
        if (!cancel) setLoading(false);
      }
    }
    load();
    timer = setInterval(load, POLL_INTERVAL_MS);
    return () => {
      cancel = true;
      if (timer) clearInterval(timer);
    };
  }, []);

  if (!enabled || alerts.length === 0) return null;

  const counts = {
    critical: alerts.filter((a) => a.severidad === "critical").length,
    warning: alerts.filter((a) => a.severidad === "warning").length,
    info: alerts.filter((a) => a.severidad === "info").length,
  };
  const badgeColor =
    counts.critical > 0 ? "#EF4444" : counts.warning > 0 ? "#F59E0B" : "#60A5FA";

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative inline-flex items-center justify-center rounded-md p-1.5 transition-colors"
        style={{ color: alerts.length > 0 ? "#D77F70" : undefined }}
        aria-label="Alertas Caonabo IA"
      >
        <Sparkles size={16} className={alerts.length > 0 ? "" : "text-text-muted hover:text-text"} />
        {alerts.length > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center text-[9px] font-bold text-white rounded-full"
            style={{
              minWidth: 14,
              height: 14,
              padding: "0 4px",
              background: badgeColor,
            }}
          >
            {alerts.length > 9 ? "9+" : alerts.length}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-40 w-80 max-h-96 overflow-y-auto rounded-xl border bg-surface shadow-lg"
          style={{ borderColor: "rgba(139,58,46,0.30)" }}
        >
          <div
            className="flex items-center justify-between px-3 py-2 border-b border-border"
            style={{ background: "rgba(139,58,46,0.08)" }}
          >
            <div className="flex items-center gap-1.5">
              <Sparkles size={12} style={{ color: "#D77F70" }} />
              <span className="text-xs font-bold" style={{ color: "#D77F70" }}>
                Caonabo · Alertas ({alerts.length})
              </span>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-text-muted hover:text-text"
            >
              <X size={12} />
            </button>
          </div>
          <ul className="divide-y divide-border">
            {alerts.map((a, i) => (
              <li key={i} className="px-3 py-2 hover:bg-surface-hover/40 cursor-default">
                <div className="flex items-start gap-2">
                  <SevIcon sev={a.severidad} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-text">{a.titulo}</p>
                    <p className="text-[10px] text-text-muted mt-0.5">{a.mensaje}</p>
                    <p className="text-[9px] text-text-muted/70 mt-0.5">
                      {a.modulo} · {a.tipo}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
          <div className="px-3 py-1.5 text-[9px] text-text-muted text-center border-t border-border">
            {loading ? "Actualizando…" : "Auto-refresh cada 5 min"}
          </div>
        </div>
      )}
    </div>
  );
}

function SevIcon({ sev }: { sev: "info" | "warning" | "critical" }) {
  if (sev === "critical")
    return <AlertCircle size={12} className="text-red-400 shrink-0 mt-0.5" />;
  if (sev === "warning")
    return <AlertTriangle size={12} className="text-amber-400 shrink-0 mt-0.5" />;
  return <Info size={12} className="text-blue-400 shrink-0 mt-0.5" />;
}
